import React, { useState, useCallback, useMemo } from 'react'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcDateRangePicker,
  DcDiv,
  DcFlexBox,
  DcText,
  PixelPageContainer,
  Section,
  SectionContent,
  colors,
} from 'pixel-base'

import { useUrlFilters } from 'pixel-base'
import DeadStockChart from './components/inventory/DeadStockChart'

import LeastCountChart from './components/inventory/LeastCountChart'
import MostCountChart from './components/inventory/MostCountChart'
import RunRateChart from './components/inventory/RunRateChart'
import SellingFasterChart from './components/inventory/SellingFasterChart'
import SellingSlowerChart from './components/inventory/SellingSlowerChart'
import StockTurnoverChart from './components/inventory/StockTurnoverChart'
import { InventoryProduct } from './components/inventory/types'
import { useInventoryData } from './components/inventory/useInventoryData'
import { useInventoryStats } from './components/inventory/useInventoryStats'

const breadcrumbs = [
  {
    name: 'Analytics',
    active: false,
  },
  {
    name: 'Inventory Dashboard',
    active: true,
  },
]

interface ChartFilters {
  start_date: Date
  end_date: Date
  data_type: string
}

const InventoryDashboard = () => {
  const { filters, updateFilters } = useUrlFilters()
  const [date, setDate] = useState<ChartFilters>(filters)

  const handleOnChange = useCallback(
    (startDate: Date, endDate: Date, dataType: string) => {
      const newFilters = {
        start_date: startDate,
        end_date: endDate,
        data_type: dataType || 'daily',
      }
      setDate(newFilters)
      updateFilters(newFilters)
    },
    [updateFilters],
  )

  const handleProductClick = useCallback((_product: InventoryProduct) => {
    // Handle product click - could open product details modal
    // Product details: _product
  }, [])

  // Create stable filter objects to prevent unnecessary re-renders
  const mostCountFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'desc' as const,
    }),
    [],
  )

  const leastCountFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'asc' as const,
    }),
    [],
  )

  const sellingFasterFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'desc' as const,
    }),
    [],
  )

  const sellingSlowerFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'asc' as const,
    }),
    [],
  )

  const runRateFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'asc' as const,
    }),
    [],
  )

  const stockTurnoverFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'desc' as const,
    }),
    [],
  )

  const deadStockFilters = useMemo(
    () => ({
      limit: 10,
      sort_order: 'desc' as const,
    }),
    [],
  )

  // Fetch inventory stats
  const inventoryStats = useInventoryStats()

  // Fetch data for each report type
  const mostCountData = useInventoryData('most-count', mostCountFilters)
  const leastCountData = useInventoryData('least-count', leastCountFilters)
  const runRateData = useInventoryData('run-rate', runRateFilters)
  const sellingFasterData = useInventoryData(
    'selling-faster',
    sellingFasterFilters,
  )
  const sellingSlowerData = useInventoryData(
    'selling-slower',
    sellingSlowerFilters,
  )
  const deadStockData = useInventoryData('dead-stock', deadStockFilters)
  const stockTurnoverData = useInventoryData(
    'stock-turnover',
    stockTurnoverFilters,
  )

  return (
    <PixelPageContainer maxHeight="100vh" height="100vh" padding="0px">
      <DcFlexBox
        height="60px"
        style={{
          fontSize: '16px',
          padding: '8px 0px',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
          backgroundColor: colors.white,
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
        >
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <Section style={{ padding: '0px' }}>
        <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
          <DcFlexBox
            height="auto"
            padding="5px 20px"
            justifyContent="space-between"
          >
            <DcFlexBox
              height="auto"
              gap="5px"
              style={{ width: '70%' }}
              justifyContent="flex-start"
            />
            <DcFlexBox height="auto" justifyContent="flex-end" width="30%">
              <DcDateRangePicker
                size="sm"
                month={2}
                ranges={{
                  start_date: date.start_date,
                  end_date: date.end_date,
                }}
                onApply={() => {
                  const newFilters = {
                    start_date: date.start_date,
                    end_date: date.end_date,
                    data_type: date.data_type || 'daily',
                  }
                  updateFilters(newFilters)
                }}
                onChange={(e) => {
                  handleOnChange(
                    e.range1.startDate,
                    e.range1.endDate,
                    e.range1.data_type || 'daily',
                  )
                }}
              />
            </DcFlexBox>
          </DcFlexBox>
        </SectionContent>
      </Section>

      <DcFlexBox style={{ height: 'calc(100vh - 100px)' }}>
        <Section
          style={{
            display: 'flex',
            padding: '20px',
            height: 'calc(100vh - 100px)',
            flexDirection: 'column',
            overflowY: 'scroll',
            gap: '20px',
          }}
        >
          {/* Top Row - 3 Performance Gauges */}
          <Section
            style={{
              zIndex: 10,
              padding: '0px',
              marginBottom: '20px',
            }}
          >
            <DcFlexBox gap="20px" height="auto">
              <DcDiv style={{ width: '33.33%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Overall Stock Health
                </DcText>
                <LeastCountChart
                  data={leastCountData.data?.data || []}
                  loading={leastCountData.loading}
                  error={leastCountData.error}
                  onProductClick={handleProductClick}
                  showGaugeOnly={true}
                  stats={inventoryStats.data?.data}
                />
              </DcDiv>
              <DcDiv style={{ width: '33.33%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Velocity Performance
                </DcText>
                <SellingFasterChart
                  data={sellingFasterData.data?.data || []}
                  loading={sellingFasterData.loading}
                  error={sellingFasterData.error}
                  onProductClick={handleProductClick}
                  showGaugeOnly={true}
                  stats={inventoryStats.data?.data}
                />
              </DcDiv>
              <DcDiv style={{ width: '33.33%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Turnover Performance
                </DcText>
                <StockTurnoverChart
                  data={stockTurnoverData.data?.data || []}
                  loading={stockTurnoverData.loading}
                  error={stockTurnoverData.error}
                  onProductClick={handleProductClick}
                  showGaugeOnly={true}
                  stats={inventoryStats.data?.data}
                />
              </DcDiv>
            </DcFlexBox>
          </Section>

          {/* Second Row - Bar Charts Side by Side */}
          <Section
            style={{
              zIndex: 10,
              padding: '0px',
              marginBottom: '20px',
            }}
          >
            <DcFlexBox gap="20px" height="auto">
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Top Products by Stock Count
                </DcText>
                <MostCountChart
                  data={mostCountData.data?.data || []}
                  loading={mostCountData.loading}
                  error={mostCountData.error}
                  onProductClick={handleProductClick}
                  showBarChartOnly={true}
                />
              </DcDiv>
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Revenue Contribution
                </DcText>
                <SellingFasterChart
                  data={sellingFasterData.data?.data || []}
                  loading={sellingFasterData.loading}
                  error={sellingFasterData.error}
                  onProductClick={handleProductClick}
                  showBarChartOnly={true}
                />
              </DcDiv>
            </DcFlexBox>
          </Section>

          {/* Third Row - Alert Cards Side by Side */}
          <Section
            style={{
              zIndex: 10,
              padding: '0px',
              marginBottom: '20px',
            }}
          >
            <DcFlexBox gap="20px" height="auto">
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.warning,
                  }}
                >
                  ⚠️ Low Stock Alerts
                </DcText>
                <LeastCountChart
                  data={leastCountData.data?.data || []}
                  loading={leastCountData.loading}
                  error={leastCountData.error}
                  onProductClick={handleProductClick}
                  showCardsOnly={true}
                />
              </DcDiv>
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.danger,
                  }}
                >
                  🚨 Dead Stock Alerts
                </DcText>
                <DeadStockChart
                  data={deadStockData.data?.data || []}
                  loading={deadStockData.loading}
                  error={deadStockData.error}
                  onProductClick={handleProductClick}
                  showCardsOnly={true}
                />
              </DcDiv>
            </DcFlexBox>
          </Section>

          {/* Fourth Row - Run Rate Analysis Side by Side */}
          <Section
            style={{
              zIndex: 10,
              padding: '0px',
              marginBottom: '20px',
            }}
          >
            <DcFlexBox gap="20px" height="auto">
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Daily Run Rate Trends
                </DcText>
                <RunRateChart
                  data={runRateData.data?.data || []}
                  loading={runRateData.loading}
                  error={runRateData.error}
                  onProductClick={handleProductClick}
                  showLineChartOnly={true}
                />
              </DcDiv>
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Stock vs Run Rate Correlation
                </DcText>
                <RunRateChart
                  data={runRateData.data?.data || []}
                  loading={runRateData.loading}
                  error={runRateData.error}
                  onProductClick={handleProductClick}
                  showScatterPlotOnly={true}
                />
              </DcDiv>
            </DcFlexBox>
          </Section>

          {/* Fifth Row - Correlation Analysis Side by Side */}
          <Section
            style={{
              zIndex: 10,
              padding: '0px',
              marginBottom: '20px',
            }}
          >
            <DcFlexBox gap="20px" height="auto">
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Inventory Value vs Sales Velocity
                </DcText>
                <SellingSlowerChart
                  data={sellingSlowerData.data?.data || []}
                  loading={sellingSlowerData.loading}
                  error={sellingSlowerData.error}
                  onProductClick={handleProductClick}
                  showScatterPlotOnly={true}
                />
              </DcDiv>
              <DcDiv style={{ width: '50%' }}>
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: colors.text,
                  }}
                >
                  Inventory Value vs Turnover Ratio
                </DcText>
                <StockTurnoverChart
                  data={stockTurnoverData.data?.data || []}
                  loading={stockTurnoverData.loading}
                  error={stockTurnoverData.error}
                  onProductClick={handleProductClick}
                  showScatterPlotOnly={true}
                />
              </DcDiv>
            </DcFlexBox>
          </Section>
        </Section>
      </DcFlexBox>
    </PixelPageContainer>
  )
}

export default InventoryDashboard
